import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { CheckCircle2, AlertCircle, Clock, ArrowRight, Shield } from 'lucide-react';
import { fetchUserProfile } from '@/services/profile-service';

const StripeOnboardingCard: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [stripeStatus, setStripeStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadStripeStatus();
    }
  }, [user?.id]);

  const loadStripeStatus = async () => {
    try {
      const profile = await fetchUserProfile(user!.id);
      setStripeStatus(profile?.stripe_account_status || null);
    } catch (error) {
      console.error('Error loading Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setOnboarding(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-connect-account');

      if (error) throw error;

      if (data.url) {
        // Redirect to Stripe onboarding
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error('Onboarding error:', error);
      toast({
        title: 'Failed to start onboarding',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
      setOnboarding(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse h-24 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  // Status: Active (KYC Complete)
  if (stripeStatus === 'active') {
    return (
      <Card className="border-green-500/30 bg-gradient-to-br from-green-500/5 to-emerald-500/10">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <CardTitle className="text-lg">Stripe Account Active</CardTitle>
          </div>
          <CardDescription>
            You're verified and can receive payouts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="w-4 h-4" />
            <span>KYC verified • Ready to earn</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Status: Pending (KYC In Progress)
  if (stripeStatus === 'pending') {
    return (
      <Card className="border-orange-500/30 bg-gradient-to-br from-orange-500/5 to-yellow-500/10">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-lg">Verification Pending</CardTitle>
          </div>
          <CardDescription>
            Complete your Stripe verification to claim drops
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your Stripe account is being verified. This usually takes a few minutes.
          </p>
          <Button 
            onClick={handleStartOnboarding}
            disabled={onboarding}
            className="w-full"
          >
            {onboarding ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Loading...
              </>
            ) : (
              <>
                Continue Verification
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Status: Restricted or Disabled
  if (stripeStatus === 'restricted' || stripeStatus === 'disabled') {
    return (
      <Card className="border-red-500/30 bg-gradient-to-br from-red-500/5 to-pink-500/10">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <CardTitle className="text-lg">Account Issue</CardTitle>
          </div>
          <CardDescription>
            There's an issue with your Stripe account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Please contact support or check your Stripe dashboard for more details.
          </p>
          <Button 
            onClick={handleStartOnboarding}
            variant="outline"
            className="w-full"
          >
            Retry Verification
          </Button>
        </CardContent>
      </Card>
    );
  }

  // No Stripe Account (Not Started)
  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-purple-500/10">
      <CardHeader>
        <CardTitle className="text-xl">Complete KYC to Claim Drops</CardTitle>
        <CardDescription>
          Verify your identity with Stripe to start earning from drops
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-primary">1</span>
            </div>
            <div>
              <div className="font-medium">Connect Stripe Express</div>
              <div className="text-sm text-muted-foreground">
                Create your Stripe account in minutes
              </div>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-primary">2</span>
            </div>
            <div>
              <div className="font-medium">Verify Your Identity</div>
              <div className="text-sm text-muted-foreground">
                Secure KYC process managed by Stripe
              </div>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-primary">3</span>
            </div>
            <div>
              <div className="font-medium">Start Claiming Drops</div>
              <div className="text-sm text-muted-foreground">
                Earn from drops and receive payouts
              </div>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Shield className="w-4 h-4" />
            <span className="font-medium">Why KYC?</span>
          </div>
          <p className="text-xs text-muted-foreground">
            KYC verification is required to comply with financial regulations and ensure secure payouts. Your information is protected by Stripe.
          </p>
        </div>

        <Button 
          onClick={handleStartOnboarding}
          disabled={onboarding}
          className="w-full"
          size="lg"
        >
          {onboarding ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
              Starting...
            </>
          ) : (
            <>
              Start Verification
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          Powered by <strong>Stripe</strong> • Secure & Fast
        </p>
      </CardContent>
    </Card>
  );
};

export default StripeOnboardingCard;
